import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";

const faqs = [
  {
    q: "Wie läuft eine Session ab?",
    a: "Nach deiner Buchung erhältst du einen kurzen Vorbereitungsfragebogen, damit ich mich optimal vorbereiten kann. Die Session selbst findet per Telefon oder Video statt und dauert je nach Leistung 45–60 Minuten. Du bist dabei bequem zu Hause.",
  },
  {
    q: "Ist eine Remote-Session genauso wirksam wie eine Präsenzsitzung?",
    a: "Ja. Energetische Arbeit ist nicht an physische Nähe gebunden. Die Wirksamkeit von Remote-Sessions ist vergleichbar mit Präsenzsitzungen — viele Klienten berichten sogar von tieferer Entspannung in der vertrauten eigenen Umgebung.",
  },
  {
    q: "Wie bereite ich mich auf eine Session vor?",
    a: "Sorge für eine ruhige Umgebung, in der du nicht gestört wirst. Du kannst dir vorab überlegen, was dich aktuell bewegt oder welche Themen du ansprechen möchtest. Achte darauf, ausreichend Wasser zu trinken und dich nach der Session Zeit zu geben.",
  },
  {
    q: "Wie erhalte ich meine Buchungsbestätigung?",
    a: "Nach deiner Buchung erhältst du eine automatische Bestätigung per E-Mail. Ich melde mich anschließend persönlich bei dir, um den genauen Termin zu bestätigen und den Vorbereitungsfragebogen zu senden.",
  },
  {
    q: "Kann ich mehrere Sessions buchen?",
    a: "Ja, selbstverständlich. Viele Klienten arbeiten regelmäßig mit mir zusammen, um tiefgreifende und nachhaltige Veränderungen zu erleben. Bei mehreren Sessions empfehle ich einen zeitlichen Abstand von mindestens 1–2 Wochen.",
  },
  {
    q: "Wie läuft die Tierkommunikation ab?",
    a: "Du sendest mir vorab ein aktuelles Foto deines Tieres sowie dessen Namen, Alter und deine konkreten Fragen oder Anliegen. In der Session nehme ich energetischen Kontakt zu deinem Tier auf und teile die empfangenen Eindrücke, Gefühle und Botschaften mit dir.",
  },
];

function FAQItem({ item, index }) {
  const [open, setOpen] = useState(false);

  return (
    <motion.div
      className="border-b border-[#1A1A2E]"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.07 }}
    >
      <button
        className="w-full flex items-center justify-between py-6 text-left group"
        onClick={() => setOpen(!open)}
        data-testid={`faq-item-${index}`}
      >
        <span
          className={`text-lg font-light transition-colors duration-300 ${
            open ? "text-[#C9A84C]" : "text-[#F0EDE8] group-hover:text-[#C9A84C]"
          }`}
          style={{ fontFamily: "Cormorant Garamond, serif" }}
        >
          {item.q}
        </span>
        <motion.div
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.3 }}
          className={`flex-shrink-0 ml-4 transition-colors ${
            open ? "text-[#C9A84C]" : "text-[#9A9A9A]"
          }`}
        >
          <ChevronDown size={18} />
        </motion.div>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            className="overflow-hidden"
          >
            <p className="text-[#9A9A9A] text-sm leading-relaxed pb-6 pr-8">
              {item.a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function FAQ() {
  return (
    <div className="bg-[#050510] text-[#F0EDE8] min-h-screen pt-32 pb-24">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <p className="text-[0.65rem] uppercase tracking-[0.4em] text-[#C9A84C] mb-4">
            FAQ
          </p>
          <h1
            className="text-5xl md:text-6xl font-light text-[#F0EDE8]"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            Häufige Fragen
          </h1>
        </motion.div>

        <div data-testid="faq-list">
          {faqs.map((item, i) => (
            <FAQItem key={i} item={item} index={i} />
          ))}
        </div>

        <motion.div
          className="mt-16 text-center p-10 bg-[#0D0D1A] border border-[#1A1A2E] rounded-2xl"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <p
            className="text-2xl font-light text-[#F0EDE8] mb-4"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            Noch weitere Fragen?
          </p>
          <p className="text-[#9A9A9A] text-sm mb-8">
            Ich beantworte gerne alle offenen Fragen persönlich.
          </p>
          <a
            href="/kontakt"
            className="btn-gold-shimmer border border-[#C9A84C] text-[#C9A84C] text-xs uppercase tracking-[0.2em] px-10 py-4 hover:bg-[#C9A84C] hover:text-[#050510] transition-all duration-300 inline-block"
            data-testid="faq-kontakt-btn"
          >
            Kontakt aufnehmen
          </a>
        </motion.div>
      </div>
    </div>
  );
}
